import { useState } from 'react';
import { Tag, Download, Trash2, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { spatialApi, exportApi, getErrorMessage } from '../../lib/api';

interface BatchOperationsBarProps {
    selectedIds: Set<string>;
    totalCount: number;
    onToggleSelectAll: () => void;
    onClearSelection: () => void;
    onRefresh: () => void;
}

export default function BatchOperationsBar({
    selectedIds,
    totalCount,
    onToggleSelectAll,
    onClearSelection,
    onRefresh,
}: BatchOperationsBarProps) {
    const [batchDeleting, setBatchDeleting] = useState(false);
    const [batchTagging, setBatchTagging] = useState(false);
    const [tagInput, setTagInput] = useState('');

    const handleBatchDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`¿Eliminar ${selectedIds.size} polígono(s) seleccionado(s)?`)) return;

        setBatchDeleting(true);
        try {
            const result = await spatialApi.batchDelete({ polygon_ids: Array.from(selectedIds) });
            toast.success(`${result.deleted} polígono(s) eliminado(s)`);
            onClearSelection();
            onRefresh();
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error al eliminar');
        } finally {
            setBatchDeleting(false);
        }
    };

    const handleBatchTag = async () => {
        if (selectedIds.size === 0 || !tagInput.trim()) return;
        try {
            const tags = tagInput.split(',').map((t) => t.trim()).filter(Boolean);
            const result = await spatialApi.batchTag({
                polygon_ids: Array.from(selectedIds),
                tags,
                action: 'add',
            });
            toast.success(`Etiquetas aplicadas a ${result.updated} polígono(s)`);
            setBatchTagging(false);
            setTagInput('');
            onRefresh();
        } catch (err: any) {
            toast.error(getErrorMessage(err) || 'Error al etiquetar');
        }
    };

    if (selectedIds.size === 0) return null;

    return (
        <div className="sticky top-0 z-40 bg-primary-600 text-white rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 flex-wrap shadow-lg animate-fade-in">
            <div className="flex items-center gap-3">
                <span className="font-medium text-sm">{selectedIds.size} seleccionado(s)</span>
                <button
                    onClick={onToggleSelectAll}
                    className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded"
                >
                    {selectedIds.size === totalCount ? 'Deseleccionar todo' : 'Seleccionar todo'}
                </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                {batchTagging ? (
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            placeholder="etiqueta1, etiqueta2..."
                            className="px-2 py-1 rounded text-sm text-gray-900 w-32 md:w-48"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleBatchTag(); }}
                        />
                        <button
                            onClick={handleBatchTag}
                            disabled={!tagInput.trim()}
                            className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded"
                        >
                            Aplicar
                        </button>
                        <button
                            onClick={() => { setBatchTagging(false); setTagInput(''); }}
                            className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ) : (
                    <>
                        <button
                            onClick={() => setBatchTagging(true)}
                            className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded"
                        >
                            <Tag className="w-3 h-3" />
                            Etiquetar
                        </button>
                        <button
                            onClick={() => exportApi.downloadPolygons('geojson')}
                            className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded"
                        >
                            <Download className="w-3 h-3" />
                            Exportar
                        </button>
                        <button
                            onClick={handleBatchDelete}
                            disabled={batchDeleting}
                            className="flex items-center gap-1 text-xs bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded"
                        >
                            {batchDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            Eliminar
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
